import { ZuruflexApiError } from '@zuruflex/api-client';
import type { CarOwner } from '@zuruflex/types';
import { formatPhone } from '@zuruflex/utils';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@zuruflex/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageCircle, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

/**
 * Individuals Zuruflex lists cars for, on the first-party account.
 *
 * Each is asked on WhatsApp about reservations for the cars they are linked
 * to — from the car's own page — alongside the team, and the first answer
 * wins. None of them has a login, so there is nothing here about access.
 */
export function CarOwnersCard({ partnerId }: { partnerId: string }) {
  const { t } = useTranslation(['admin', 'common']);
  const { user } = useAuth();
  const canEdit = user?.role === 'OWNER' || user?.role === 'MANAGER';
  const owners = useQuery({
    queryKey: ['car-owners', partnerId],
    queryFn: () => api.carOwners(partnerId),
  });
  // `null` is the add form; an owner is the edit form.
  const [editing, setEditing] = useState<CarOwner | null | undefined>(undefined);

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>{t('carOwners.title')}</CardTitle>
          <CardDescription>{t('carOwners.hint')}</CardDescription>
        </div>
        {canEdit ? (
          <Button size="sm" variant="outline" onClick={() => setEditing(null)}>
            <Plus className="size-4" />
            {t('carOwners.add')}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {owners.data?.length === 0 ? (
          <p className="text-sm text-text-muted">{t('carOwners.empty')}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {(owners.data ?? []).map((owner) => (
              <li
                key={owner.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-field border border-border-default px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{owner.fullName}</p>
                  <p className="flex items-center gap-1 truncate text-xs text-text-muted">
                    <MessageCircle className="size-3" />
                    {formatPhone(owner.whatsappPhone ?? owner.phone ?? '')}
                  </p>
                  {owner.vehicles.length > 0 ? (
                    <p className="mt-1 flex flex-wrap gap-x-2 text-xs">
                      {owner.vehicles.map((vehicle) => (
                        <Link key={vehicle.id} to={`/fleet/${vehicle.id}`} className="underline">
                          {vehicle.make} {vehicle.model} · {vehicle.plateNumber}
                        </Link>
                      ))}
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-text-muted">{t('carOwners.noCars')}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {owner.isActive ? null : (
                    <Badge variant="outline">{t('carOwners.inactive')}</Badge>
                  )}
                  {canEdit ? (
                    <Button size="sm" variant="ghost" onClick={() => setEditing(owner)}>
                      {t('carOwners.edit')}
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      {editing !== undefined ? (
        <CarOwnerDialog
          partnerId={partnerId}
          owner={editing}
          onClose={() => setEditing(undefined)}
        />
      ) : null}
    </Card>
  );
}

function CarOwnerDialog({
  partnerId,
  owner,
  onClose,
}: {
  partnerId: string;
  owner: CarOwner | null;
  onClose: () => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState({
    fullName: owner?.fullName ?? '',
    phone: owner?.phone ?? '',
    whatsappPhone: owner?.whatsappPhone ?? '',
    email: owner?.email ?? '',
    isActive: owner?.isActive ?? true,
  });
  const set = <K extends keyof typeof draft>(key: K, value: (typeof draft)[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const save = useMutation({
    mutationFn: () => {
      const body = {
        fullName: draft.fullName.trim(),
        phone: draft.phone.trim(),
        whatsappPhone: draft.whatsappPhone.trim() || null,
        email: draft.email.trim() || null,
      };
      return owner
        ? api.updateCarOwner(partnerId, owner.id, { ...body, isActive: draft.isActive })
        : api.addCarOwner(partnerId, body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['car-owners', partnerId] });
      onClose();
    },
  });

  const ready = draft.fullName.trim().length >= 2 && draft.phone.trim().length >= 6;

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{owner ? t('carOwners.editTitle') : t('carOwners.addTitle')}</DialogTitle>
          <DialogDescription>{t('carOwners.formHint')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <FormField label={t('carOwners.fullName')} required>
            {(field) => (
              <Input
                {...field}
                value={draft.fullName}
                onChange={(event) => set('fullName', event.target.value)}
              />
            )}
          </FormField>
          <FormField label={t('carOwners.phone')} hint={t('carOwners.phoneHint')} required>
            {(field) => (
              <Input
                {...field}
                type="tel"
                placeholder="+233201234567"
                value={draft.phone}
                onChange={(event) => set('phone', event.target.value)}
              />
            )}
          </FormField>
          <FormField label={t('carOwners.whatsappPhone')} hint={t('carOwners.whatsappPhoneHint')}>
            {(field) => (
              <Input
                {...field}
                type="tel"
                value={draft.whatsappPhone}
                onChange={(event) => set('whatsappPhone', event.target.value)}
              />
            )}
          </FormField>
          <FormField label={t('carOwners.email')} hint={t('carOwners.emailHint')}>
            {(field) => (
              <Input
                {...field}
                type="email"
                value={draft.email}
                onChange={(event) => set('email', event.target.value)}
              />
            )}
          </FormField>
          {owner ? (
            <label className="flex items-center justify-between gap-3 text-sm">
              <span>
                <span className="font-medium">{t('carOwners.active')}</span>
                <span className="block text-xs text-text-muted">{t('carOwners.activeHint')}</span>
              </span>
              <Switch
                checked={draft.isActive}
                onCheckedChange={(checked) => set('isActive', checked)}
              />
            </label>
          ) : null}

          {save.error ? (
            <Alert variant="danger">
              {save.error instanceof ZuruflexApiError
                ? save.error.message
                : t('common:error.unexpected')}
            </Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common:action.cancel')}
          </Button>
          <Button isLoading={save.isPending} disabled={!ready} onClick={() => save.mutate()}>
            {owner ? t('common:action.save') : t('carOwners.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Radix's Select cannot hold an empty string, so "nobody" needs a name. */
const NONE = 'none';

/**
 * Which individual a first-party car is listed for. Their WhatsApp gets this
 * car's reservations, alongside the team's.
 */
export function VehicleOwnerCard({
  partnerId,
  vehicleId,
}: {
  partnerId: string;
  vehicleId: string;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canEdit = user?.role === 'OWNER' || user?.role === 'MANAGER';
  const owners = useQuery({
    queryKey: ['car-owners', partnerId],
    queryFn: () => api.carOwners(partnerId),
  });
  const current = owners.data?.find((owner) =>
    owner.vehicles.some((vehicle) => vehicle.id === vehicleId),
  );

  const link = useMutation({
    mutationFn: (ownerContactId: string | null) =>
      api.setVehicleOwner(partnerId, vehicleId, ownerContactId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['car-owners', partnerId] }),
  });

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>{t('carOwners.vehicleTitle')}</CardTitle>
          <CardDescription>{t('carOwners.vehicleHint')}</CardDescription>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <Select
          value={current?.id ?? NONE}
          disabled={!canEdit || link.isPending || owners.isPending}
          onValueChange={(value) => link.mutate(value === NONE ? null : value)}
        >
          <SelectTrigger aria-label={t('carOwners.vehicleTitle')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>{t('carOwners.none')}</SelectItem>
            {(owners.data ?? [])
              .filter((owner) => owner.isActive || owner.id === current?.id)
              .map((owner) => (
                <SelectItem key={owner.id} value={owner.id}>
                  {owner.fullName} · {formatPhone(owner.whatsappPhone ?? owner.phone ?? '')}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        {owners.data?.length === 0 ? (
          <p className="text-xs text-text-muted">
            <Link to={`/partners/${partnerId}`} className="underline">
              {t('carOwners.addFirst')}
            </Link>
          </p>
        ) : null}
        {link.error ? (
          <Alert variant="danger">
            {link.error instanceof ZuruflexApiError
              ? link.error.message
              : t('common:error.unexpected')}
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}
